import { Link } from 'react-router-dom';
import {
  Heart,
  CalendarCheck,
  Phone,
  Sparkle,
  MapPin,
  CheckCircle,
  Quotes,
  Star,
  ChatCircleDots,
} from '@phosphor-icons/react';
import { BRAND, WEDDING_IMAGES } from '../lib/brand';

export const WeddingOfficiantPage = () => {
  const ceremonyTypes = [
    {
      title: "Elopements",
      description: "Just the two of you, a witness or two, and a ceremony built around your story. Courthouse, hilltop, or honky-tonk — wherever your love story unfolds.",
      icon: Heart,
    },
    {
      title: "Micro-Weddings",
      description: "Intimate ceremonies for 20 guests or fewer. Personalized vows, family blessings, and the warmth of a celebration that feels just like you.",
      icon: Sparkle,
    },
    {
      title: "County Clerk Ceremonies",
      description: "A meaningful, dignified ceremony coordinated with your Tennessee County Clerk — never rushed, never generic. Your moment deserves more than a stamp.",
      icon: CalendarCheck,
    },
    {
      title: "Signatures Only",
      description: "A full ceremony isn't always required. If you're getting married abroad, planning a ceremony later, or simply need your marriage license legally signed and finalized — India can handle just the paperwork.",
      icon: CheckCircle,
    },
    {
      title: "Vow Renewals",
      description: "Celebrate the chapters you've already written together. A heartfelt renewal for milestone anniversaries or simply because you choose each other again.",
      icon: Star,
    },
  ];

  const includedItems = [
    "Personalized ceremony script written with you",
    "Pre-ceremony planning consultation",
    "Officiant for the entire ceremony",
    "Marriage license signing & filing",
    "Pronouncement & first kiss moment",
    "Optional ring exchange & vow guidance",
    "Available statewide across Tennessee",
    "Same-week and emergency availability",
  ];

  return (
    <main data-testid="wedding-officiant-page">
      {/* Hero with wedding image */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${WEDDING_IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#4A0000]/95 via-[#800000]/60 to-[#800000]/30" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">Wedding Officiant Services</span>
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-[1.05] mb-6 max-w-3xl drop-shadow-2xl">
            Your Love Story, <span className="italic gold-shimmer">Officiated.</span>
          </h1>
          <p className="text-lg sm:text-xl text-white max-w-2xl mb-10 drop-shadow-lg">
            Personalized, heartfelt ceremonies across Middle Tennessee — from County Clerk signings to hilltop micro-weddings, no two are ever the same.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/booking"
              data-testid="wedding-book-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-8 py-4 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-2xl"
            >
              <CalendarCheck weight="bold" size={20} />
              Reserve Your Date
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-3 border border-white/60 backdrop-blur-sm bg-white/10 text-white hover:bg-white hover:text-[#800000] transition-all px-8 py-4 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={20} />
              {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Intro section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            {/* Image */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#D4AF37]/15 rounded-sm transform rotate-2" />
                <img
                  src={WEDDING_IMAGES.couple}
                  alt="Couple celebrating their wedding ceremony with India's Inking Service"
                  className="relative w-full aspect-[4/5] object-cover rounded-sm shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white px-6 py-4 shadow-xl border-l-4 border-[#D4AF37] hidden sm:block">
                  <p className="font-serif-display text-2xl text-[#800000] italic">Felicia & Husband</p>
                  <p className="text-xs text-[#5C5C5C] mt-1 tracking-wide">Officiated by India · Nashville</p>
                </div>
              </div>
            </div>
            {/* Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-3 mb-5">
                <div className="h-px w-8 bg-[#D4AF37]" />
                <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">A Different Kind Of Officiant</span>
              </div>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-[#800000] mb-6 leading-tight">
                Your day deserves <span className="italic">more than a script.</span>
              </h2>
              <div className="space-y-5 text-[#1A1A1A] leading-relaxed text-base">
                <p>
                  When India officiates your wedding, you're not getting a stranger reading from a generic template. You're getting a <strong className="text-[#800000]">Nashville native, an MBA, and a small-business owner</strong> who takes the time to understand your story before she writes a single word.
                </p>
                <p>
                  Whether you're eloping on a Wednesday afternoon, signing your marriage license at the County Clerk's office with two witnesses, or hosting a 30-person ceremony in a backyard string-light fairyland — India brings warmth, polish, and a personal touch to every "I do."
                </p>
                <p>
                  And because she's also a Tennessee Notary Public, your <strong className="text-[#800000]">marriage license is filed correctly the first time</strong>. No surprises, no follow-up, no missing paperwork.
                </p>
              </div>

              {/* Pull quote */}
              <div className="mt-10 pl-6 border-l-4 border-[#D4AF37]">
                <Quotes weight="fill" size={28} className="text-[#D4AF37] mb-3" />
                <p className="font-serif-display text-2xl italic text-[#800000] leading-relaxed">
                  "I'm glad my husband and I got the opportunity to have her officiate our wedding!"
                </p>
                <p className="text-sm text-[#5C5C5C] mt-2 tracking-wide">— Felicia Bryson · Google Review</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ceremony types */}
      <section className="py-24 bg-[#FBF8F1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">Every Kind Of Love</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-[#800000] mb-4">
              Ceremonies <span className="italic">tailored to you.</span>
            </h2>
            <p className="text-[#5C5C5C] text-base sm:text-lg">
              Big, small, simple, elaborate — every couple's love story is different. Your ceremony should be too.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-6 lg:max-w-6xl lg:mx-auto">
            {ceremonyTypes.map((c, i) => {
              const lgSpan = 'lg:col-span-2';
              // Top row: items 0, 1, 2 span 2 cols each (6/3). Bottom row: items 3, 4 — start item 3 at col 2 to center the pair.
              const bottomCenter =
                i === 3 ? 'lg:col-start-2' : '';
              return (
                <div
                  key={i}
                  data-testid={`ceremony-type-${i}`}
                  className={`bg-white border border-[#E8E2D5] rounded-sm p-7 hover:border-[#D4AF37]/50 hover:shadow-xl transition-all duration-500 ${lgSpan} ${bottomCenter}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mb-5">
                    <c.icon weight="duotone" size={24} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="font-serif-display text-xl font-semibold text-[#800000] mb-3 leading-tight">
                    {c.title}
                  </h3>
                  <p className="text-[#5C5C5C] text-sm leading-relaxed">
                    {c.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">A Glimpse</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-[#800000] mb-3">
              Moments <span className="italic">made meaningful.</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-lg group">
              <img src={WEDDING_IMAGES.ceremony} alt="Wedding ceremony" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#800000]/40 to-transparent" />
            </div>
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-lg group lg:translate-y-8">
              <img src={WEDDING_IMAGES.rings} alt="Wedding rings" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#800000]/40 to-transparent" />
            </div>
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-lg group">
              <img src={WEDDING_IMAGES.outdoor} alt="Outdoor wedding ceremony" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#800000]/40 to-transparent" />
            </div>
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-lg group lg:translate-y-8">
              <img src={WEDDING_IMAGES.bouquet} alt="Wedding bouquet" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#800000]/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-24 bg-[#FBF8F1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">What's Included</p>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-[#800000] mb-6 leading-tight">
                Everything you need, <span className="italic">nothing you don't.</span>
              </h2>
              <p className="text-[#5C5C5C] text-base leading-relaxed mb-8">
                A ceremony that's truly yours — plus the legal precision of a Tennessee Notary Public to make sure your marriage license is filed correctly and on time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/booking"
                  className="inline-flex items-center gap-3 bg-[#800000] text-white hover:bg-[#5C0000] transition-all px-8 py-4 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-lg"
                >
                  <CalendarCheck weight="bold" size={18} />
                  Reserve Your Date
                </Link>
              </div>
            </div>
            <div>
              <ul className="space-y-3">
                {includedItems.map((item, i) => (
                  <li
                    key={i}
                    data-testid={`included-item-${i}`}
                    className="flex items-start gap-3 bg-white border border-[#E8E2D5] rounded-sm p-4 hover:border-[#D4AF37]/50 transition-colors"
                  >
                    <CheckCircle weight="fill" size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1A1A1A] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Booth upsell card */}
      <section data-testid="photo-booth-section" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            data-testid="photo-booth-card"
            className="relative bg-gradient-to-br from-[#FBF8F1] via-white to-[#FBF8F1] border border-[#D4AF37]/40 rounded-sm overflow-hidden shadow-2xl"
          >
            {/* Decorative gold accent corners */}
            <div className="absolute top-0 left-0 w-24 h-24 border-t-2 border-l-2 border-[#D4AF37] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-24 h-24 border-b-2 border-r-2 border-[#D4AF37] pointer-events-none" />

            <div className="grid lg:grid-cols-12 gap-0 lg:gap-10 items-stretch">
              {/* Real image */}
              <div className="lg:col-span-5 relative min-h-[300px] lg:min-h-[480px]">
                <img
                  src="https://images.squarespace-cdn.com/content/v1/620e8e3b6f7b375c5cbd9d40/1706815826496-QSF0DPJ3QQ674IN0I55O/SalsaPromoHighRes007%2B%281%29%2Bcopy.png"
                  alt="Sleek digital iPad photo booth in action"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#800000]/30 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-xl flex items-center gap-2">
                  <ChatCircleDots weight="fill" size={16} className="text-[#D4AF37]" />
                  <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#800000]">
                    Instant Text Delivery
                  </span>
                </div>
              </div>

              {/* Copy */}
              <div className="lg:col-span-7 p-8 sm:p-12 lg:py-14 lg:pr-14 lg:pl-4">
                <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                  Add-On · Premium Experience
                </p>
                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] mb-5 leading-tight">
                  Sleek Digital <span className="italic">Photo Booth Experience.</span>
                </h2>
                <p className="text-[#1A1A1A] text-base sm:text-lg leading-[1.85] mb-7 max-w-2xl">
                  Enhance your special day with our premium, fully digital iPad photo booth. Guests can seamlessly capture memories and instantly receive high-quality photos directly to their phones via text message. Cumbersome setups and printing lines are gone — just pure, instant, digital fun.
                </p>

                <div className="flex flex-wrap gap-2.5 mb-8">
                  {[
                    'Premium iPad Setup',
                    'Instant Text Delivery',
                    'No Printer Lines',
                    'Cloud Gallery Included',
                  ].map((feature) => (
                    <span
                      key={feature}
                      className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white border border-[#E8E2D5] rounded-sm text-xs font-medium text-[#800000]"
                    >
                      <CheckCircle weight="fill" size={14} className="text-[#D4AF37]" />
                      {feature}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/booking?service=wedding-officiant&photoBooth=1"
                    data-testid="photo-booth-cta"
                    className="inline-flex items-center justify-center gap-3 bg-[#800000] text-white hover:bg-[#5C0000] transition-all px-7 py-3.5 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
                  >
                    <CalendarCheck weight="bold" size={18} />
                    Book Wedding + Photo Booth
                  </Link>
                  <Link
                    to="/services/photo-booth"
                    data-testid="photo-booth-learn-more"
                    className="inline-flex items-center justify-center gap-2 border border-[#800000] text-[#800000] hover:bg-[#800000] hover:text-white transition-all px-7 py-3.5 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase"
                  >
                    Learn More
                  </Link>
                </div>
                <p className="text-xs text-[#5C5C5C] mt-4 italic">
                  Combined wedding-officiant + photo-booth intake — one form, one date.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage + final CTA */}
      <section className="py-20 bg-[#800000] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <MapPin weight="duotone" size={48} className="text-[#D4AF37] mx-auto mb-5" />
          <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold text-white mb-4">
            Available across <span className="italic gold-shimmer">all of Tennessee.</span>
          </h2>
          <p className="text-lg text-white/85 mb-10 max-w-2xl mx-auto leading-relaxed">
            From downtown Nashville to the rolling hills of Williamson County and beyond — wherever you say "I do," India will meet you there.
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-10 py-4 rounded-sm font-semibold text-sm tracking-[0.15em] uppercase shadow-2xl"
          >
            <Heart weight="fill" size={22} />
            Reserve Your Wedding Date
          </Link>
        </div>
      </section>
    </main>
  );
};
